import { ChangeEvent, Fragment, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import Select from 'react-select';
import { useNavigate, useParams } from 'react-router-dom';
import { Container } from '@/components/container';
import { KeenIcon } from '@/components';
import { useSnackbar } from 'notistack';
import { useLayout } from '@/providers';
import { useGetIconImagesQuery } from '@/services/IconImagesManagementService';
import { IconImageStatus } from '@/types/icon-images-management.types';
import {
  useCreateListValueMutation,
  useGetAdminListValuesQuery,
  useUpdateListValueMutation,
} from '@/services/ListValueService';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { ListValueStatus, ManagedListType } from '@/types/list-value.types';
import {
  DEFAULT_MANAGED_LIST_TYPE,
  EMPTY_FORM,
  FormErrors,
  isKeyLockedListType,
  isManagedListType,
  LIST_TYPE_CONFIG,
  ListValueFormState,
  sanitizePayload,
  supportsDisplayOrder,
  supportsListValueIcon,
  getStatusLabel,
  toFormState,
  validateForm,
} from './listValueManagement.shared';

interface ListValueFormPageProps {
  mode: 'create' | 'edit';
}

type IconSelectOption = {
  value: string;
  label: string;
  imageUrl: string;
};

const ListValueFormPage = ({ mode }: ListValueFormPageProps) => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { type: routeType, id } = useParams<{ type: string; id?: string }>();
  const type = isManagedListType(routeType) ? routeType : null;

  const [formState, setFormState] = useState<ListValueFormState>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const {
    data: values = [],
    isLoading: isLoadingValues,
    isError: isValuesError,
  } = useGetAdminListValuesQuery(
    { type: type ?? DEFAULT_MANAGED_LIST_TYPE },
    {
      skip: !type,
    }
  );
  const { data: poiCategories = [], isLoading: isLoadingPoiCategories } =
    useGetAdminListValuesQuery(
      { type: ManagedListType.POI_CATEGORY },
      {
        skip: type !== ManagedListType.POI_SUBTYPE,
      }
    );
  const {
    data: serviceCategories = [],
    isLoading: isLoadingServiceCategories,
  } = useGetAdminListValuesQuery(
    { type: ManagedListType.SERVICE_CATEGORY },
    {
      skip: type !== ManagedListType.SERVICE_SUB_CATEGORY,
    }
  );

  const existingValue = useMemo(
    () => values.find((value) => String(value.id) === id),
    [id, values]
  );
  const poiCategoryOptions = useMemo(
    () =>
      poiCategories.map((category) => ({
        value: String(category.id),
        label: category.label_en || category.key,
      })),
    [poiCategories]
  );
  const serviceCategoryOptions = useMemo(
    () =>
      serviceCategories.map((category) => ({
        value: category.key,
        label: category.label_en || category.key,
      })),
    [serviceCategories]
  );
  const isPoiSubtype = type === ManagedListType.POI_SUBTYPE;
  const isServiceSubCategory = type === ManagedListType.SERVICE_SUB_CATEGORY;
  const isKeyLockedType = isKeyLockedListType(type);
  const usesIconField = supportsListValueIcon(type);
  const { data: iconImagesResponse, isLoading: isLoadingIcons } =
    useGetIconImagesQuery(
      { lang: 'en' },
      {
        skip: !usesIconField,
      }
    );
  const iconOptions = useMemo(() => {
    const icons = iconImagesResponse?.data || [];
    const activeIcons = icons.filter(
      (icon) => icon.status === IconImageStatus.ACTIVE
    );
    const selectedRetiredIcon =
      formState.icon_id &&
      icons.find(
        (icon) =>
          String(icon.id) === formState.icon_id &&
          icon.status === IconImageStatus.RETIRED
      );

    const selectableIcons = selectedRetiredIcon
      ? [...activeIcons, selectedRetiredIcon]
      : activeIcons;

    return selectableIcons.map((icon) => ({
      value: String(icon.id),
      label: icon.name,
      imageUrl: icon.imageUrl,
    }));
  }, [iconImagesResponse, formState.icon_id]);
  const selectedIconOption = useMemo(
    () =>
      iconOptions.find((option) => option.value === formState.icon_id) || null,
    [formState.icon_id, iconOptions]
  );

  const [createListValue, { isLoading: isCreating }] =
    useCreateListValueMutation();
  const [updateListValue, { isLoading: isUpdating }] =
    useUpdateListValueMutation();

  useEffect(() => {
    if (mode === 'create') {
      setFormState(EMPTY_FORM);
      setFormErrors({});
      return;
    }

    if (existingValue) {
      setFormState(toFormState(existingValue));
      setFormErrors({});
    }
  }, [existingValue, mode]);

  const handleBack = () => {
    navigate(
      type
        ? `/list-value-management?type=${encodeURIComponent(type)}`
        : '/list-value-management'
    );
  };

  const handleFormChange = (field: keyof ListValueFormState, value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async () => {
    if (!type) return;

    const nextErrors = validateForm(formState, type);
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      return;
    }

    const payload = sanitizePayload(formState, type);

    try {
      if (mode === 'create') {
        await createListValue({
          type,
          data: payload,
        }).unwrap();
        enqueueSnackbar('List value created successfully.', {
          variant: 'success',
        });
      } else if (existingValue) {
        await updateListValue({
          type,
          id: String(existingValue.id),
          data: payload,
        }).unwrap();
        enqueueSnackbar('List value updated successfully.', {
          variant: 'success',
        });
      }

      handleBack();
    } catch (error: unknown) {
      const apiMessage =
        error &&
        typeof error === 'object' &&
        'data' in error &&
        error.data &&
        typeof error.data === 'object' &&
        'message' in error.data
          ? String((error.data as { message?: string | string[] }).message)
          : null;
      enqueueSnackbar(apiMessage || 'Failed to save list value.', {
        variant: 'error',
      });
    }
  };

  const renderSelectInput = (
    field: keyof ListValueFormState,
    label: string,
    selectOptions: Array<{ value: string; label: string }>,
    options?: { placeholder?: string; disabled?: boolean }
  ) => (
    <div className="space-y-2">
      <label
        htmlFor={field}
        className="block text-sm font-medium text-gray-700"
      >
        {label}
      </label>
      <select
        id={field}
        value={formState[field]}
        onChange={(e) => handleFormChange(field, e.target.value)}
        disabled={isCreating || isUpdating || options?.disabled}
        className={clsx(
          'w-full rounded-lg border bg-light-active px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500',
          formErrors[field]
            ? 'border-red-500 contrast:border-[#FF8B33]'
            : 'border-gray-300'
        )}
        aria-invalid={!!formErrors[field]}
        aria-describedby={formErrors[field] ? `${field}-error` : undefined}
      >
        <option value="">{options?.placeholder || `Select ${label}`}</option>
        {selectOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {formErrors[field] && (
        <p id={`${field}-error`} className="text-xs text-red-500" role="alert">
          {formErrors[field]}
        </p>
      )}
    </div>
  );

  const renderIconSelectInput = (
    field: keyof ListValueFormState,
    label: string,
    selectOptions: IconSelectOption[],
    options?: { placeholder?: string; disabled?: boolean }
  ) => (
    <div className="space-y-2">
      <label
        htmlFor={field}
        className="block text-sm font-medium text-gray-700"
      >
        {label}
      </label>
      <Select<IconSelectOption, false>
        inputId={field}
        options={selectOptions}
        value={selectedIconOption}
        onChange={(selected) => handleFormChange(field, selected?.value || '')}
        isDisabled={isCreating || isUpdating || options?.disabled}
        isClearable
        placeholder={options?.placeholder || `Select ${label}`}
        className={clsx(
          'react-select-container',
          formErrors[field] && 'react-select-container--error'
        )}
        classNamePrefix="react-select"
        formatOptionLabel={(option) => (
          <div className="flex items-center gap-3">
            <img
              src={option.imageUrl}
              alt={option.label}
              className="h-8 w-8 rounded object-contain border border-gray-200 bg-white p-1"
            />
            <span className="text-sm text-gray-700">{option.label}</span>
          </div>
        )}
        styles={{
          control: (base) => ({
            ...base,
            borderRadius: '0.5rem',
            borderColor: formErrors[field] ? '#ef4444' : '#d1d5db',
            minHeight: '2.75rem',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            boxShadow: 'none',
            '&:hover': {
              borderColor: formErrors[field] ? '#ef4444' : '#93c5fd',
            },
          }),
          menu: (base) => ({
            ...base,
            zIndex: 50,
          }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused ? '#eff6ff' : base.backgroundColor,
            color: '#374151',
          }),
          singleValue: (base) => ({
            ...base,
            color: '#374151',
          }),
        }}
      />
      {formErrors[field] && (
        <p id={`${field}-error`} className="text-xs text-red-500" role="alert">
          {formErrors[field]}
        </p>
      )}
    </div>
  );

  const renderInput = (
    field: keyof ListValueFormState,
    label: string,
    options?: {
      textarea?: boolean;
      placeholder?: string;
      inputType?: string;
      disabled?: boolean;
    }
  ) => {
    const sharedProps = {
      value: formState[field],
      onChange: (
        e: ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
      ) => handleFormChange(field, e.target.value),
      disabled: isCreating || isUpdating || options?.disabled,
      className: clsx(
        'w-full rounded-lg border bg-light-active px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500',
        formErrors[field]
          ? 'border-red-500 contrast:border-[#FF8B33]'
          : 'border-gray-300'
      ),
      'aria-invalid': !!formErrors[field],
      'aria-describedby': formErrors[field] ? `${field}-error` : undefined,
      placeholder: options?.placeholder,
    };

    return (
      <div className="space-y-2">
        <label
          htmlFor={field}
          className="block text-sm font-medium text-gray-700"
        >
          {label}
        </label>
        {field === 'status' ? (
          <select id={field} {...sharedProps}>
            {Object.values(ListValueStatus).map((status) => (
              <option key={status} value={status}>
                {getStatusLabel(status)}
              </option>
            ))}
          </select>
        ) : options?.textarea ? (
          <textarea id={field} rows={4} {...sharedProps} />
        ) : (
          <input
            id={field}
            type={options?.inputType || 'text'}
            min={options?.inputType === 'number' ? 1 : undefined}
            step={options?.inputType === 'number' ? 1 : undefined}
            {...sharedProps}
          />
        )}
        {formErrors[field] && (
          <p
            id={`${field}-error`}
            className="text-xs text-red-500"
            role="alert"
          >
            {formErrors[field]}
          </p>
        )}
      </div>
    );
  };

  if (!type) {
    return (
      <div className="container-fixed">
        <div className="card">
          <div className="card-body">
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              Invalid list value type. Please return to the list and try again.
            </div>
            <div className="mt-4">
              <button
                type="button"
                className="btn btn-light"
                onClick={handleBack}
              >
                Back to List
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'edit' && isLoadingValues) {
    return (
      <div className="container-fixed">
        <div className="card">
          <div className="card-body">
            <div className="py-4 text-center text-sm text-gray-600">
              Loading list value...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'edit' && (isValuesError || !existingValue)) {
    return (
      <div className="container-fixed">
        <div className="card">
          <div className="card-body space-y-4">
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {isValuesError
                ? 'Failed to load this list value. Please try again.'
                : 'The requested list value could not be found.'}
            </div>
            <button
              type="button"
              className="btn btn-light"
              onClick={handleBack}
            >
              Back to List
            </button>
          </div>
        </div>
      </div>
    );
  }

  const config = LIST_TYPE_CONFIG[type];
  const isSubmitting = isCreating || isUpdating;
  const title =
    mode === 'create' ? `Add ${config.title}` : `Edit ${config.title}`;
  const description =
    mode === 'create'
      ? `Create a new ${config.singularTitle} entry.`
      : `Update the selected ${config.singularTitle} entry.`;

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle text={title} />
              <ToolbarDescription>{description}</ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              <button
                type="button"
                className="btn btn-sm btn-light flex items-center gap-2 h-10"
                onClick={handleBack}
              >
                <KeenIcon icon="arrow-left" />
                Back to List
              </button>
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}

      <div className="container-fixed">
        <div className="card">
          <div className="card-header">
            <div>
              <h2 className="card-title">{title}</h2>
              <p className="mt-1 text-sm text-gray-600">{config.blurb}</p>
            </div>
          </div>
          <div className="card-body space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {renderInput('key', 'Key', {
                placeholder: 'e.g. guest_harbour',
                disabled: mode === 'edit' && isKeyLockedType,
              })}
              {isPoiSubtype &&
                renderSelectInput(
                  'category_id',
                  'POI Category',
                  poiCategoryOptions,
                  {
                    placeholder: isLoadingPoiCategories
                      ? 'Loading POI categories...'
                      : 'Select POI category',
                    disabled: isLoadingPoiCategories,
                  }
                )}
              {isServiceSubCategory &&
                renderSelectInput(
                  'category',
                  'Service Category',
                  serviceCategoryOptions,
                  {
                    placeholder: isLoadingServiceCategories
                      ? 'Loading service categories...'
                      : 'Select service category',
                    disabled: isLoadingServiceCategories,
                  }
                )}
              {usesIconField &&
                renderIconSelectInput('icon_id', 'Icon', iconOptions, {
                  placeholder: isLoadingIcons
                    ? 'Loading icons...'
                    : 'Select icon',
                  disabled: isLoadingIcons,
                })}
              {renderInput('status', 'Status')}
              {supportsDisplayOrder(type) &&
                renderInput('display_order', 'Display order', {
                  inputType: 'number',
                  placeholder: 'e.g. 10',
                })}
              {renderInput('label_en', 'English Label')}
              {renderInput('label_fi', 'Finnish Label')}
              {renderInput('label_sv', 'Swedish Label')}
              {renderInput('label_de', 'German Label')}
              {renderInput('linked_jira_issue', 'Linked Jira Issue', {
                placeholder: 'e.g. FP-123',
              })}
              <div className="md:col-span-2">
                {renderInput('description', 'Description', { textarea: true })}
              </div>
              <div className="md:col-span-2">
                {renderInput('notes', 'Notes', { textarea: true })}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t pt-4">
              <button
                type="button"
                className="btn btn-light"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? mode === 'create'
                    ? 'Creating...'
                    : 'Saving...'
                  : mode === 'create'
                    ? 'Create'
                    : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListValueFormPage;
